import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import { socketService } from '../services/socketService';
import { useAuth } from './AuthContext';
import { getTokens } from '../services/authApi';
import {
  Notification,
  NotificationCounts,
  getUnreadCounts,
  getRecentNotifications,
  markAsRead,
  markAllAsRead,
} from '../services/notificationApi';

interface CustomerAction {
  action: string;
  entityType: string;
  entityId: number;
  timestamp: string;
  [key: string]: any;
}

interface NotificationContextType {
  isConnected: boolean;
  subscribeToEntity: (type: string, id: number) => void;
  unsubscribeFromEntity: (type: string, id: number) => void;
  // Persistent notification management
  unreadCounts: NotificationCounts;
  recentNotifications: Notification[];
  refreshCounts: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  markNotificationAsRead: (notificationId: number) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Persistent notification state
  const [unreadCounts, setUnreadCounts] = useState<NotificationCounts>({
    appointment: 0,
    estimate: 0,
    work_order: 0,
    payment: 0,
    quote_request: 0,
    total: 0,
  });
  const [recentNotifications, setRecentNotifications] = useState<Notification[]>([]);

  // Connect socket when user is authenticated and is an owner
  // Use primitive values (userId, userRole) instead of entire user object to prevent unnecessary re-renders
  const userId = user?.id;
  const userRole = user?.role;

  useEffect(() => {
    console.log('👤 NotificationProvider: User changed', { userId, userRole });

    if (userId && userRole === 'owner') {
      const tokens = getTokens();
      console.log('🔑 NotificationProvider: Retrieved tokens', { hasTokens: !!tokens });

      if (tokens?.accessToken) {
        console.log('🔌 NotificationProvider: Connecting socket for owner...');
        socketService.connect(tokens.accessToken);

        const socket = socketService.getSocket();
        if (socket) {
          socket.on('connect', () => {
            console.log('✅ NotificationProvider: Socket connected event received');
            setIsConnected(true);
          });
          socket.on('disconnect', () => {
            console.log('❌ NotificationProvider: Socket disconnected event received');
            setIsConnected(false);
          });
        }
      } else {
        console.log('⚠️ NotificationProvider: No access token found');
      }
    } else {
      console.log('⚠️ NotificationProvider: User is not owner or not logged in');
    }

    return () => {
      console.log('🧹 NotificationProvider: Cleanup - disconnecting socket');
      socketService.disconnect();
      setIsConnected(false);
    };
  }, [userId, userRole]); // Use stable primitive dependencies instead of user object

  // Handle customer action notifications
  useEffect(() => {
    console.log('🔔 NotificationProvider: Setting up customer action listener', {
      isConnected,
      hasUser: !!user,
      isOwner: user?.role === 'owner',
    });

    if (!isConnected || !user || user.role !== 'owner') {
      console.log('⚠️ NotificationProvider: Skipping listener setup - not ready');
      return;
    }

    const handleCustomerAction = (data: CustomerAction) => {
      console.log('📬 Customer action received:', data);

      // Show notification based on action type
      switch (data.action) {
        case 'payment_completed':
          messageApi.success({
            content: `💰 Payment received: $${data.amount} via ${data.paymentMethod}`,
            duration: 6,
            onClick: () => {
              window.location.href = `/${user.role}/work-orders/${data.entityId}`;
            },
          });
          break;

        case 'work_order_line_item_approved':
          messageApi.info({
            content: `✅ Customer approved a line item on work order`,
            duration: 5,
            onClick: () => {
              window.location.href = `/${user.role}/work-orders/${data.entityId}`;
            },
          });
          break;

        case 'work_order_line_item_declined':
          messageApi.warning({
            content: `❌ Customer declined a line item: ${data.reason}`,
            duration: 5,
            onClick: () => {
              window.location.href = `/${user.role}/work-orders/${data.entityId}`;
            },
          });
          break;

        case 'work_order_all_approved':
          messageApi.success({
            content: `🎉 Customer approved all line items on work order!`,
            duration: 6,
            onClick: () => {
              window.location.href = `/${user.role}/work-orders/${data.entityId}`;
            },
          });
          break;

        case 'estimate_approved':
          messageApi.success({
            content: `✅ Customer approved estimate`,
            duration: 5,
            onClick: () => {
              window.location.href = `/${user.role}/estimates/${data.entityId}`;
            },
          });
          break;

        case 'estimate_declined':
          messageApi.warning({
            content: `❌ Customer declined estimate: ${data.reason}`,
            duration: 5,
            onClick: () => {
              window.location.href = `/${user.role}/estimates/${data.entityId}`;
            },
          });
          break;

        default:
          messageApi.info({
            content: `📬 New customer action: ${data.action}`,
            duration: 4,
          });
      }
    };

    console.log('✅ NotificationProvider: Attaching customer_action event listener');
    socketService.on('customer_action', handleCustomerAction);

    return () => {
      console.log('🧹 NotificationProvider: Removing customer_action event listener');
      socketService.off('customer_action', handleCustomerAction);
    };
  }, [isConnected, user, messageApi]);

  const subscribeToEntity = useCallback((type: string, id: number) => {
    socketService.subscribe(type, id);
  }, []);

  const unsubscribeFromEntity = useCallback((type: string, id: number) => {
    socketService.unsubscribe(type, id);
  }, []);

  // Fetch unread notification counts
  const refreshCounts = useCallback(async () => {
    if (!user) return;

    try {
      const counts = await getUnreadCounts();
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  }, [user]);

  // Fetch recent notifications
  const refreshNotifications = useCallback(async () => {
    if (!user) return;

    try {
      const notifications = await getRecentNotifications(10);
      setRecentNotifications(notifications);
    } catch (error) {
      console.error('Error fetching recent notifications:', error);
    }
  }, [user]);

  // Mark notification as read
  const markNotificationAsRead = useCallback(async (notificationId: number) => {
    try {
      await markAsRead(notificationId);
      await refreshCounts();
      await refreshNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [refreshCounts, refreshNotifications]);

  // Mark all notifications as read
  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      await refreshCounts();
      await refreshNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [refreshCounts, refreshNotifications]);

  // Load counts and notifications when user logs in
  // Use userId instead of user object to prevent re-running when user object changes
  useEffect(() => {
    if (userId) {
      refreshCounts();
      refreshNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Only depend on userId, not callback functions

  // Listen for Socket.IO notification events
  useEffect(() => {
    if (!isConnected || !userId) return;

    const handleNotificationCreated = (data: any) => {
      console.log('📬 New notification created:', data);
      refreshCounts();
      refreshNotifications();
    };

    const handleNotificationCountsUpdated = (counts: NotificationCounts) => {
      console.log('🔔 Notification counts updated:', counts);
      setUnreadCounts(counts);
    };

    socketService.on('notification_created', handleNotificationCreated);
    socketService.on('notification_counts_updated', handleNotificationCountsUpdated);

    return () => {
      socketService.off('notification_created', handleNotificationCreated);
      socketService.off('notification_counts_updated', handleNotificationCountsUpdated);
    };
  }, [isConnected, user, refreshCounts, refreshNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        isConnected,
        subscribeToEntity,
        unsubscribeFromEntity,
        unreadCounts,
        recentNotifications,
        refreshCounts,
        refreshNotifications,
        markNotificationAsRead,
        markAllNotificationsAsRead,
      }}
    >
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
